// BoardTV.js — BOARD GENÉRICO (RRTV): vários itens na tela ao mesmo tempo.
// ============================================================
// Generaliza o StockBoardTV pra QUALQUER fonte: ações, commodities, currencies,
// (índices também). O cue escolhe a fonte + os símbolos. Transparente 1920×1080,
// determinístico, roxo RR Magma. Estilo do card do AssetTVPage.
//
// Rota (app.js):  <Route path="/tv/board" element={<BoardTV/>} />
// CONTROLES:
//   ?source=stock|commodity|currency|index   (default stock)
//   ?symbols=PETR3,LREN3   |  BZ=F,GC=F  |  BRL,EUR  |  ^BVSP,^GSPC
//   ?title=Mais%20Negociados     título opcional
//   ?cols=1|2|3|4                colunas (default: auto)
//
// Endpoints por fonte (AJUSTE na máquina de deploy). O shape esperado está no map().

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api/go';
const LOGO_HOST = '/api/logos';
const ROXO = '#6660fe';   // RR Magma roxo
const UP = '#22c55e';
const DOWN = '#ef4444';

// >>> AJUSTE as URLs na máquina de deploy <<<
const URLS = {
  commodity: process.env.REACT_APP_COMMODITIES_URL || 'https://magma.relatorioreservado.com.br/api/go82/api/commodities',
  currency:  process.env.REACT_APP_CURRENCIES_URL  || 'https://magma.relatorioreservado.com.br/api/go82/api/currencies',
  index:     process.env.REACT_APP_INDEX_URL       || 'https://magma.relatorioreservado.com.br/api/go82/api/indices',
};

// prefixo de valor e nº de decimais por fonte
const FMT = {
  stock:     { pre: 'R$ ', dec: 2 },
  commodity: { pre: 'US$ ', dec: 2 },
  currency:  { pre: '', dec: 4 },
  index:     { pre: '', dec: 0 },
};

// normaliza um item cru → {sym, name, value, change, logo, icon}
function normalize(source, x) {
  if (source === 'stock') {
    const base = (x.sym || '').split('.')[0].toUpperCase();
    return { sym: x.sym, name: x.name, value: x.ult, change: x.var, logo: `${LOGO_HOST}/${base}.svg` };
  }
  // commodity / currency / index — mesmo shape do :8090
  return {
    sym: x.symbol, name: x.name,
    value: x.last_quote ?? x.close ?? x.price,
    change: x.rate ?? x.pct_change,
    icon: x.icon,   // pode ser URL de imagem, emoji ou vazio
  };
}

// busca os itens da fonte pros símbolos pedidos
async function loadItems(source, symbols) {
  if (source === 'stock') {
    const out = await Promise.all(symbols.map(async sym => {
      try {
        const r = await fetch(`${API_URL}/api/ticker/${sym}`);
        if (!r.ok) return null;
        const d = await r.json();
        return normalize('stock', { sym, name: d.name || sym, ult: d.ult, var: d.var });
      } catch { return null; }
    }));
    return out.filter(Boolean);
  }
  // fontes de lista única (:8090): busca tudo, filtra pelos símbolos na ORDEM pedida
  try {
    const r = await fetch(URLS[source]);
    const d = await r.json();
    const arr = Array.isArray(d) ? d : (d.data || d.indices || []);
    const by = Object.fromEntries(arr.map(x => [x.symbol, x]));
    return symbols.map(s => by[s]).filter(Boolean).map(x => normalize(source, x));
  } catch { return []; }
}

function Icon({ it, source }) {
  const [ok, setOk] = useState(true);
  const src = source === 'stock' ? it.logo : it.icon;
  if (!ok || !src) return null;
  // stock = sempre logo (caminho relativo /api/logos/..); outras fontes = img se for
  // URL/caminho, senão emoji/flag. (BUG anterior: só checava 'http' → path virava texto)
  const isImg = typeof src === 'string' &&
                (source === 'stock' || src.startsWith('http') || src.startsWith('/'));
  if (isImg) {
    return <img src={src} alt="" onError={() => setOk(false)}
                style={{ height: 72, width: 72, objectFit: 'contain', borderRadius: 8 }} />;
  }
  return <span style={{ fontSize: 64, lineHeight: 1 }}>{src}</span>;
}

function BoardCard({ it, source }) {
  const f = FMT[source] || FMT.stock;
  const v = it.change;
  const up = (v ?? 0) >= 0;
  const c = v == null ? ROXO : (up ? UP : DOWN);
  const val = it.value == null ? '—'
    : Number(it.value).toLocaleString('pt-BR', { minimumFractionDigits: f.dec, maximumFractionDigits: f.dec });
  const abs = (it.value != null && v != null) ? Math.abs(it.value - it.value / (1 + v / 100)) : null;
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      background: 'rgba(14,13,31,0.82)', border: `2px solid ${up ? UP : DOWN}33`,
      borderRadius: 18, padding: '30px 34px', backdropFilter: 'blur(8px)',
      boxShadow: '0 12px 40px rgba(0,0,0,0.5)', minWidth: 620,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 18 }}>
        <Icon it={it} source={source} />
        <div>
          <div style={{ fontFamily: "'Roble', Georgia, serif", fontWeight: 800,
                        fontSize: 42, color: ROXO, lineHeight: 1 }}>{it.name}</div>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 22,
                        color: 'rgba(255,255,255,.6)', marginTop: 6 }}>
            {(it.sym || '').replace('^', '')}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 24, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 58, fontWeight: 800, color: '#fff' }}>{f.pre}{val}</span>
        <span style={{ fontSize: 46, fontWeight: 800, color: c }}>
          {up ? '▲' : '▼'} {abs != null ? abs.toFixed(f.dec) : ''} ({(up ? '+' : '') + (v != null ? v.toFixed(2) : '—')}%)
        </span>
      </div>
    </div>
  );
}

export default function BoardTV() {
  const [params] = useSearchParams();
  const source = (params.get('source') || 'stock').toLowerCase();
  const symbols = (params.get('symbols') || '').split(',').map(s => s.trim()).filter(Boolean);
  const title = params.get('title') || '';
  const cols = Number(params.get('cols')) || (symbols.length <= 2 ? 1 : 2);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const out = await loadItems(source, symbols);
      if (alive) setRows(out);
    })();
    return () => { alive = false; };
  }, [source, symbols.join(',')]);

  return (
    <div data-source={source} style={{
      width: 1920, height: 1080, background: 'transparent', color: '#fff',
      display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
      gap: 40, padding: 80, boxSizing: 'border-box', overflow: 'hidden',
      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    }}>
      {title ? (
        <div style={{ fontFamily: "'Roble', Georgia, serif", fontWeight: 800, fontSize: 52,
                      color: '#fff', borderBottom: `4px solid ${ROXO}`, paddingBottom: 14 }}>
          {title}
        </div>
      ) : null}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 34,
                    justifyContent: 'center', alignItems: 'stretch' }}>
        {rows.map(it => <BoardCard key={it.sym} it={it} source={source} />)}
      </div>
    </div>
  );
}
